
import axios, { AxiosError } from "axios";
import type { AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { clearOnLogout, env, getToken } from "@/lib/utils";
import toast from "@/lib/toast";
import { refreshAccessToken } from "@/lib/auth.service";
import { AUTH_ROUTE_PATHS } from "@/pages/Auth/authConfig";

const config = {
  // baseURL: String(env("VITE_API_BASE_URL")) + "/api",
  baseURL: import.meta.env.VITE_API_BASE_URL + "/api",
  timeout: 30000,
};

const publicRequest = axios.create(config);
const privateRequest = axios.create(config);

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

const requestHandler = (
  request: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig => {
  const token = getToken() || "";
  request.headers = request.headers ?? {};
  request.headers.Authorization = `Bearer ${token}`;
  return request;
};

const responseErrorHandler = async (error: AxiosError) => {
  const originalRequest: any = error.config;

  if (!error.response) {
    toast.error(error.message || "Some Error Occurred");
    return Promise.reject(error);
  }

  const { status, data } = error.response;
  const message = (data as any)?.message || error.message;

  if (status === 401 && message?.toLowerCase().includes("jwt expired")) {
    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return privateRequest(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    isRefreshing = true;

    try {
      const newToken = await refreshAccessToken();
      processQueue(null, newToken);

      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return privateRequest(originalRequest);
    } catch (err) {
      processQueue(err, null);
      clearOnLogout();
      window.location.href = AUTH_ROUTE_PATHS.signIn;
      return Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  }

  if (status === 401) {
    clearOnLogout();
    window.location.href = AUTH_ROUTE_PATHS.signIn;
    toast.error("Unauthorized request");
  } else {
    toast.error(message || "Some Error Occurred");
  }

  return Promise.reject(error);
};

privateRequest.interceptors.request.use(requestHandler);
privateRequest.interceptors.response.use(
  (response: AxiosResponse) => response,
  responseErrorHandler,
);

export { privateRequest, publicRequest };
